// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useReducer, useEffect, useState, type ReactNode } from 'react';
import { message } from 'antd';
import authService from '../services/auth.service';
import useInactivityMonitor from '../hooks/useInactivityMonitor';
import InactivityWarningModal from '../components/common/InactivityWarningModal';
import type {
    AuthState,
    LoginCredentials,
    User,
    UserPermissions,
} from '../types/api.types';

/**
 * Authentication Context for AVEC Admin Dashboard
 * Provides authentication state and actions throughout the application
 */

// Auth Actions
type AuthAction =
  | { type: 'AUTH_INIT_START' }
  | { type: 'AUTH_INIT_SUCCESS'; payload: { user: User; permissions: UserPermissions } }
  | { type: 'AUTH_INIT_COMPLETE' } // New action for when initialization is done
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; permissions: UserPermissions } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_RESET' }
  | { type: 'UPDATE_USER'; payload: Partial<User> };

// Auth Context Type
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  forceLogout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
  getUserRole: () => string | null;
  canApproveCredit: (amount: number) => boolean;
  updateUserProfile: (updatedUser: Partial<User>) => void;
  error: string | null;
  clearError: () => void;
  isInitialized: boolean; // New property to track if initialization is complete
  
  // Multi-tenant helper functions
  isSuperAdmin: () => boolean;
  isCommunalAdmin: () => boolean;
  isGroupAgent: () => boolean;
  getAssignedCommuneId: () => string | null;
  getAssignedGroupId: () => string | null;
  canAccessCommune: (communeId: string) => boolean;
  canAccessGroup: (groupId: string, communeId?: string) => boolean;
}

// Initial state - Start with loading true for proper initialization
const initialState: AuthState & { error: string | null; isInitialized: boolean } = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true
  permissions: null,
  error: null,
  isInitialized: false, // Track initialization state
};

// Auth reducer
function authReducer(
  state: AuthState & { error: string | null; isInitialized: boolean },
  action: AuthAction
): AuthState & { error: string | null; isInitialized: boolean } {
  switch (action.type) {
    case 'AUTH_INIT_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        isInitialized: false,
      };

    case 'AUTH_INIT_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: authService.getAccessToken() ? { 
          accessToken: authService.getAccessToken()!, 
          refreshToken: authService.getRefreshToken()! 
        } : null,
        isAuthenticated: true,
        isLoading: false,
        permissions: action.payload.permissions,
        error: null,
        isInitialized: true,
      };

    case 'AUTH_INIT_COMPLETE':
      return {
        ...state,
        isLoading: false,
        isInitialized: true,
      };

    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: authService.getAccessToken() ? { 
          accessToken: authService.getAccessToken()!, 
          refreshToken: authService.getRefreshToken()! 
        } : null,
        isAuthenticated: true,
        isLoading: false,
        permissions: action.payload.permissions,
        error: null,
        isInitialized: true,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: null,
        error: action.payload,
        isInitialized: true,
      };

    case 'AUTH_LOGOUT':
      return {
        ...initialState,
        isLoading: false,
        error: null,
        isInitialized: true, // Keep initialized as true after logout
      };

    case 'AUTH_RESET':
      return {
        ...initialState,
        isLoading: false,
        isInitialized: true,
      };

    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };

    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  
  // Inactivity monitoring state
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      dispatch({ type: 'AUTH_INIT_START' });
      
      try {
        // Add a small delay to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const authState = authService.getAuthState();
        
        console.log('Initializing auth state:', authState);
        
        if (authState.isAuthenticated && authState.user) {
          console.log('User is authenticated, setting auth success');
          dispatch({
            type: 'AUTH_INIT_SUCCESS',
            payload: {
              user: authState.user,
              permissions: authState.permissions || ({} as UserPermissions),
            },
          });
        } else {
          console.log('User is not authenticated, completing init');
          dispatch({ type: 'AUTH_INIT_COMPLETE' });
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        dispatch({
          type: 'AUTH_FAILURE',
          payload: `Échec de l'initialisation de l'authentification`,
        });
      }
    };

    initializeAuth();
  }, []);

  // Listen for logout events from other tabs or auth service
  useEffect(() => {
    const handleLogout = () => {
      console.log('Logout event received');
      dispatch({ type: 'AUTH_LOGOUT' });
      
      // Redirect to login page after logout
      setTimeout(() => {
        if (window.location.pathname !== '/login') {
          console.log('Redirecting to login page after logout');
          window.location.href = '/login';
        }
      }, 100);
    };

    window.addEventListener('auth:logout', handleLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  // Login function
  const login = async (credentials: LoginCredentials): Promise<void> => {
    console.log('AuthContext: Starting login with credentials:', credentials);
    dispatch({ type: 'AUTH_START' });
    
    try {
      console.log('AuthContext: Calling authService.login');
      const loginResponse = await authService.login(credentials);
      
      console.log('AuthContext: Login response received:', loginResponse);
      
      const permissions = loginResponse.user.Staff?.permissions || 
                         loginResponse.user.profile?.permissions || 
                         ({} as UserPermissions);
      
      console.log('AuthContext: Extracted permissions:', permissions);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: loginResponse.user,
          permissions,
        },
      });

      console.log('AuthContext: Dispatched AUTH_SUCCESS');
      
      // Verify auth state after login
      setTimeout(() => {
        const authState = authService.getAuthState();
        console.log('AuthContext: Auth state after login:', authState);
      }, 100);

      message.success(`Connexion réussie ! Bon retour.`);
    } catch (error: any) {
      const errorMessage = error.message || `Connexion échouée. Veuillez réessayer.`;
      console.error('AuthContext: Login failed with error:', error);
      console.error('AuthContext: Error message:', errorMessage);
      
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      message.error(errorMessage);
      throw error; // Re-throw for component handling
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    console.log('AuthContext: Starting logout process');
    
    try {
      // Call the auth service logout which handles the API call
      await authService.logout();
      
      // Dispatch logout action to update state
      dispatch({ type: 'AUTH_LOGOUT' });
      
      console.log('AuthContext: Logout successful');
      message.success(`Déconnexion réussie`);
      
      // The auth service will trigger the 'auth:logout' event which will handle navigation
      
    } catch (error: any) {
      console.error('AuthContext: Logout error:', error);
      
      // Even if server logout fails, clear local state
      dispatch({ type: 'AUTH_LOGOUT' });
      
      // Show appropriate message based on error type
      if (error.error?.code === 'NETWORK_ERROR') {
        message.warning(`Déconnecté localement. Connexion réseau indisponible.`);
      } else {
        message.warning(`Déconnecté localement. Certains nettoyages serveur ont pu échouer.`);
      }
      
      // The auth service will trigger the 'auth:logout' event which will handle navigation
    }
  };

  // Force logout function (for emergency cases)
  const forceLogout = async (): Promise<void> => {
    console.log('AuthContext: Force logout initiated');
    
    try {
      await authService.forceLogout();
      dispatch({ type: 'AUTH_LOGOUT' });
      message.info(`Déconnecté`);
      
      // Force redirect to login
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
      
    } catch (error) {
      console.error('AuthContext: Force logout error:', error);
      // Still try to clear state and redirect
      dispatch({ type: 'AUTH_LOGOUT' });
      window.location.href = '/login';
    }
  };

  // Permission checking functions
  const hasPermission = (permission: string): boolean => {
    return authService.hasPermission(permission);
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return authService.hasAnyPermission(permissions);
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    return authService.hasAllPermissions(permissions);
  };

  // Admin check
  const isAdmin = (): boolean => {
    return authService.isAdmin();
  };

  // Get user role
  const getUserRole = (): string | null => {
    return authService.getUserRole();
  };

  // Multi-tenant helper functions
  const isSuperAdmin = (): boolean => {
    const role = state.user?.Staff?.role || state.user?.profile?.role || state.user?.staffRole;
    
    // Handle both new super_admin role and legacy administrator role
    // Removed scopeLevel === 'legacy' condition as it incorrectly catches communal admins
    return role === 'super_admin' || role === 'administrator';
  };

  const isCommunalAdmin = (): boolean => {
    const role = state.user?.Staff?.role || state.user?.profile?.role || state.user?.staffRole;
    return role === 'communal_admin';
  };

  const isGroupAgent = (): boolean => {
    const role = state.user?.Staff?.role || state.user?.profile?.role || state.user?.staffRole;
    return role === 'group_agent';
  };

  const getAssignedCommuneId = (): string | null => {
    return state.user?.Staff?.assigned_commune_id || 
           state.user?.profile?.assigned_commune_id || 
           state.user?.assignedCommuneId || 
           null;
  };

  const getAssignedGroupId = (): string | null => {
    return state.user?.Staff?.assigned_group_id || 
           state.user?.profile?.assigned_group_id || 
           state.user?.assignedGroupId || 
           null;
  };

  const canAccessCommune = (communeId: string): boolean => {
    if (isSuperAdmin()) return true;
    if (isCommunalAdmin()) return getAssignedCommuneId() === communeId;
    return false;
  };

  const canAccessGroup = (groupId: string, communeId?: string): boolean => {
    if (isSuperAdmin()) return true;
    if (isGroupAgent()) return getAssignedGroupId() === groupId;
    if (isCommunalAdmin() && communeId) return getAssignedCommuneId() === communeId;
    return false;
  };

  // Credit approval check
  const canApproveCredit = (amount: number): boolean => {
    return authService.canApproveCredit(amount);
  };

  // Update user profile
  const updateUserProfile = (updatedUser: Partial<User>): void => {
    authService.updateUserProfile(updatedUser);
    dispatch({ type: 'UPDATE_USER', payload: updatedUser });
  };

  // Inactivity monitoring handlers
  const handleInactivityWarning = () => {
    console.log('AuthProvider: Inactivity warning triggered');
    setShowInactivityWarning(true);
  };

  const handleInactivityLogout = async () => {
    console.log('AuthProvider: Inactivity logout triggered');
    setShowInactivityWarning(false);
    message.warning('Déconnecté pour inactivité');
    await logout();
  };

  const handleContinueSession = () => {
    console.log('AuthProvider: User chose to continue session');
    setShowInactivityWarning(false);
    // The inactivity monitor will automatically reset timers when this function is called
  };

  // Set up inactivity monitoring
  const inactivityMonitor = useInactivityMonitor({
    isActive: state.isAuthenticated && state.isInitialized,
    onWarning: handleInactivityWarning,
    onLogout: handleInactivityLogout,
  });

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'AUTH_RESET' });
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    forceLogout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    getUserRole,
    canApproveCredit,
    updateUserProfile,
    clearError,
    isInitialized: state.isInitialized,
    
    // Multi-tenant helper functions
    isSuperAdmin,
    isCommunalAdmin,
    isGroupAgent,
    getAssignedCommuneId,
    getAssignedGroupId,
    canAccessCommune,
    canAccessGroup,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <InactivityWarningModal
        visible={showInactivityWarning}
        onContinue={handleContinueSession}
        onLogout={handleInactivityLogout}
        countdownSeconds={60}
      />
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;